import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { getHandoff, submitDecision, completeHandoff, reportMissing, openRegistration, closeRegistration, PUBLIC_TOKEN } from "@/lib/api";

function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="mh-seg" data-testid={testid} role="tablist">
      {options.map((o) => (
        <button key={String(o.v)} type="button" role="tab" aria-selected={value === o.v}
          className={value === o.v ? "on" : ""} onClick={() => onChange(o.v)} data-testid={`${testid}-${o.v}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function recordedBy(dec) {
  return dec?.submittedBy && dec.submittedBy.trim() ? dec.submittedBy : null;
}

function decisionSummary(dec) {
  if (!dec) return "";
  if (dec.showed === false) return "No Show";
  return { hire: "Hire Approved", future_consideration: "Future Consideration", not_selected: "Not Selected" }[dec.decision] || "Recorded";
}

// ---------------- View 2: Candidate decision (active or correction) ----------------
function CandidateDecision({ c, config, index, total, correcting, onBack, onSaved }) {
  const d = correcting ? c.decision : null;
  const [showed, setShowed] = useState(d ? d.showed : null);
  const [decision, setDecision] = useState(d && d.decision !== "no_show" ? d.decision : null);
  const [hlo, setHlo] = useState(d?.hlo || false);
  const [position, setPosition] = useState(d?.position || "");
  const [shift, setShift] = useState(d?.shift || "");
  const [supervisorId, setSupervisorId] = useState(d?.supervisorId || "");
  const [startDate, setStartDate] = useState(d?.startDate || "");
  const [pending, setPending] = useState(d?.startDatePending || false);
  const [reconsiderDate, setReconsiderDate] = useState(d?.reconsiderDate || "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  const needsHire = showed === true && decision === "hire";
  const needsFuture = showed === true && decision === "future_consideration";
  const suggestSup = config.supervisors.find((s) => s.shift && s.shift === shift);
  const valid = showed === false
    || (showed === true && !!decision
        && (!needsHire || (position && shift && supervisorId && (startDate || pending)))
        && (!needsFuture || !!reconsiderDate.trim()));

  const save = async () => {
    if (!valid || busy || saved) return;
    setErr(""); setBusy(true);
    const sup = config.supervisors.find((s) => s.id === supervisorId);
    try {
      const r = await submitDecision(c.token, c.registrationId, {
        showed, decision: showed ? decision : "no_show", hlo,
        position: needsHire ? position : null, shift: needsHire ? shift : null,
        supervisorId: needsHire ? supervisorId : null, supervisorLabel: needsHire ? sup?.label : null,
        startDate: needsHire && !pending ? startDate : null, startDatePending: needsHire ? pending : false,
        reconsiderDate: needsFuture ? reconsiderDate.trim() : null,
        submittedBy: c.submittedBy, override: !!correcting, baseVersion: c.decision?.submittedAt,
      });
      const nr = {
        showed, decision: showed ? decision : "no_show", hlo, position, shift, supervisorId,
        supervisorLabel: needsHire ? sup?.label : null, startDate, startDatePending: pending,
        reconsiderDate: needsFuture ? reconsiderDate.trim() : null,
        submittedBy: c.submittedBy, submittedAt: r.submittedAt,
      };
      setSaved(true);
      onSaved(c.registrationId, nr);
      // Movement itself confirms the save — return to the roster after a brief "Saved".
      setTimeout(onBack, 620);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const detail = Array.isArray(raw) ? (raw[0]?.msg || "Validation error") : (typeof raw === "string" ? raw : "Could not save.");
      setBusy(false);
      setErr(detail);
      if (e?.response?.status === 409 && c.onRefresh) { await c.onRefresh(); setTimeout(onBack, 1100); }
    }
  };

  return (
    <div className="mh-detail" data-testid="candidate-decision">
      <div className="mh-appbar">
        <button className="mh-back" onClick={onBack} data-testid="back-to-roster"><ChevronLeft size={18} /> All candidates</button>
        <span className="mh-appbar-progress">{index} of {total}</span>
      </div>
      <div className="mh-chead">
        <h1 className="mh-cname" data-testid="detail-name">{c.name}</h1>
        <div className="mh-cid">MIHP ID · {c.shortId}</div>
      </div>

      {correcting && <div className="mh-correcting">Correcting a previously recorded decision. This is saved with an audit trail.</div>}

      <label className="mh-lbl">Did they attend?</label>
      <Segmented options={[{ v: true, label: "Yes" }, { v: false, label: "No" }]} value={showed}
        onChange={(v) => { setShowed(v); if (v === false) { setDecision(null); setHlo(false); } }} testid="attend" />

      {showed === false && <p className="mh-help">Recording as <strong>No Show</strong> — this stays in the candidate's history and moves them to <strong>Archived — Inactive</strong>. No further detail needed.</p>}

      {showed === true && (
        <>
          <label className="mh-lbl">Decision</label>
          <Segmented options={[{ v: "hire", label: "Hire" }, { v: "future_consideration", label: "Future" }, { v: "not_selected", label: "Not Selected" }]}
            value={decision} onChange={setDecision} testid="decision" />

          {needsHire && (
            <div className="mh-fields">
              <div className="mh-field"><label>Position</label>
                <select value={position} onChange={(e) => setPosition(e.target.value)} data-testid="position">
                  <option value="">Select…</option>{config.positions.map((p) => <option key={p}>{p}</option>)}
                </select></div>
              <div className="mh-field"><label>Shift</label>
                <select value={shift} onChange={(e) => setShift(e.target.value)} data-testid="shift">
                  <option value="">Select…</option>{config.shifts.map((p) => <option key={p}>{p}</option>)}
                </select></div>
              <div className="mh-field"><label>Assigned supervisor {suggestSup && <span className="mh-suggest">· suggested {suggestSup.label}</span>}</label>
                <select value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)} data-testid="supervisor">
                  <option value="">Select…</option>{config.supervisors.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select></div>
              <div className="mh-field"><label>Start date</label>
                <input type="date" value={startDate} disabled={pending} onChange={(e) => setStartDate(e.target.value)} data-testid="startdate" />
                <label className="mh-chk"><input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} data-testid="pending" /> To be confirmed</label>
              </div>
              <div className="mh-field"><label>Assignment type</label>
                <label className="mh-chk"><input type="checkbox" checked={hlo} onChange={(e) => setHlo(e.target.checked)} data-testid="hlo-check" /> Higher-Level Opportunity (HLO)</label>
                {hlo && <p className="mh-help" style={{ marginTop: 6 }}>Recorded on this hire. Make It Happen Philly confirms any higher-level compensation or approval internally.</p>}
              </div>
              <p className="mh-help">Recording <strong>Hire</strong> approves this candidate for employment through Make It Happen Philly. Onboarding is completed separately — Hire does not mean onboarding is finished.</p>
            </div>
          )}
          {needsFuture && (
            <div className="mh-fields">
              <div className="mh-field"><label>Reconsider by (date or timeframe)</label>
                <input type="text" value={reconsiderDate} onChange={(e) => setReconsiderDate(e.target.value)} placeholder="e.g. Oct 2026 or a specific date" data-testid="reconsider-date" />
              </div>
              <p className="mh-help">Recorded as <strong>Future Consideration</strong> — no current vacancy. Kept as a ready, previously-interviewed candidate to reconsider then.</p>
            </div>
          )}
        </>
      )}

      {err && <div className="mh-err" data-testid="detail-error">{err}</div>}

      <button className="mh-save" disabled={!valid || busy || saved} onClick={save} data-testid="save-candidate">
        {saved ? "Saved" : busy ? "Saving…" : "Save candidate"}
      </button>
    </div>
  );
}

// ---------------- View 3: Completed candidate (read-only) ----------------
function CompletedSummary({ c, index, total, onBack, onCorrect }) {
  const d = c.decision;
  const pairs = [["Decision", decisionSummary(d)]];
  if (recordedBy(d)) pairs.push(["Recorded by", d.submittedBy]);
  if (d.showed && d.decision === "hire") {
    if (d.position) pairs.push(["Position", d.position]);
    if (d.shift) pairs.push(["Shift", d.shift]);
    if (d.supervisorLabel) pairs.push(["Assigned supervisor", d.supervisorLabel]);
    pairs.push(["Start date", d.startDatePending ? "To be confirmed" : (d.startDate || "—")]);
    if (d.hlo) pairs.push(["Higher-Level Opportunity", "Yes"]);
  }
  if (d.showed && d.decision === "future_consideration" && d.reconsiderDate) {
    pairs.push(["Reconsider by", d.reconsiderDate]);
  }
  return (
    <div className="mh-detail" data-testid="candidate-summary">
      <div className="mh-appbar">
        <button className="mh-back" onClick={onBack} data-testid="back-to-roster"><ChevronLeft size={18} /> All candidates</button>
        <span className="mh-appbar-progress">{index} of {total}</span>
      </div>
      <div className="mh-completed-badge"><Check size={15} /> Interview completed</div>
      <div className="mh-chead">
        <h1 className="mh-cname" data-testid="detail-name">{c.name}</h1>
        <div className="mh-cid">MIHP ID · {c.shortId}</div>
      </div>
      <div className="mh-summary">
        {pairs.map(([k, v]) => (
          <div className="mh-pair" key={k} data-testid={`summary-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}><span>{k}</span><strong>{v}</strong></div>
        ))}
      </div>
      <button className="mh-secondary" onClick={onCorrect} data-testid="correct-record">Correct this record</button>
    </div>
  );
}

// ---------------- Main: routes between roster and candidate views ----------------
export default function EmployerHandoff() {
  const { token: tokenParam } = useParams();
  const token = tokenParam || PUBLIC_TOKEN;
  const [data, setData] = useState(null);
  const [nf, setNf] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("decision");
  const [correcting, setCorrecting] = useState(false);
  const [supBy, setSupBy] = useState("");
  const [missing, setMissing] = useState(false);
  const [mName, setMName] = useState(""); const [mNote, setMNote] = useState("");
  const scrollRef = useRef(0);
  const [regBusy, setRegBusy] = useState(false);

  const toggleReg = async () => {
    if (regBusy) return;
    setRegBusy(true);
    try {
      if (data?.registrationOpen) await closeRegistration(token);
      else await openRegistration(token);
      await load(false);
    } finally { setRegBusy(false); }
  };
  const RegBar = () => (
    <div className="mh-regbar" data-testid="onsite-registration-control">
      <div>
        <div className="mh-regbar-title">On-Site / Same-Day Interview Registration</div>
        <div className="mh-regbar-state" data-testid="registration-state">
          {data?.registrationOpen ? <><span className="dot-live" /> Registration Open</> : "Registration Closed"}
        </div>
      </div>
      <button className={`btn ${data?.registrationOpen ? "danger-outline" : "primary"}`} disabled={regBusy} onClick={toggleReg} data-testid="toggle-registration-btn">
        {data?.registrationOpen ? "Close Registration" : "Open Registration"}
      </button>
    </div>
  );

  const load = async (isPoll = false) => {
    try { const d = await getHandoff(token); setData(d); }
    catch { if (!isPoll) setNf(true); }
  };
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => {
    const iv = setInterval(() => { if (!document.hidden) load(true); }, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSaved = (id, dec) =>
    setData((prev) => (prev ? { ...prev, candidates: prev.candidates.map((c) => (c.registrationId === id ? { ...c, decision: dec } : c)) } : prev));

  const openCandidate = (c) => {
    scrollRef.current = window.scrollY;
    setSelected(c.registrationId);
    setMode(c.decision ? "summary" : "decision");
    setCorrecting(false);
    window.scrollTo(0, 0);
  };
  const backToRoster = () => {
    setSelected(null); setCorrecting(false);
    requestAnimationFrame(() => window.scrollTo(0, scrollRef.current));
  };

  if (nf) return <div className="mh"><div className="mh-empty">This interview link is not valid.</div></div>;
  if (!data) return <div className="mh"><div className="mh-empty">Loading…</div></div>;

  if (receipt) {
    const rc = receipt.counts;
    return (
      <div className="mh"><div className="mh-wrap"><div className="mh-success" data-testid="handoff-receipt">
        <div className="mh-success-ic"><Check size={28} /></div>
        <h3>Interview Handoff Complete</h3>
        <p><strong>Make It Happen Philly</strong> has securely received your interview decisions.</p>
        <div className="mh-summary" style={{ marginTop: 24, textAlign: "left" }}>
          <div className="mh-pair"><span>Candidates updated</span><strong>{rc.updated}</strong></div>
          {rc.hire ? <div className="mh-pair"><span>Hire Approved</span><strong>{rc.hire}</strong></div> : null}
          {rc.future_consideration ? <div className="mh-pair"><span>Future Consideration</span><strong>{rc.future_consideration}</strong></div> : null}
          {rc.not_selected ? <div className="mh-pair"><span>Not Selected</span><strong>{rc.not_selected}</strong></div> : null}
          {rc.no_show ? <div className="mh-pair"><span>No Show</span><strong>{rc.no_show}</strong></div> : null}
        </div>
        <button className="mh-complete-btn" onClick={() => window.location.reload()} data-testid="handoff-done">Done</button>
      </div></div></div>
    );
  }

  if (data.status === "no_active_session") return (
    <div className="mh" data-testid="no-active-session"><div className="mh-wrap">
      <header className="mh-head">
        <div className="mh-logo"><img src="/mihp-logo.png" alt="Make It Happen Philly" /></div>
        <div className="mh-agency">MAKE IT HAPPEN PHILLY</div>
        <h1 className="mh-headtitle">Interview Handoff</h1>
        <div className="mh-headsub">{data.employerName}</div>
      </header>
      <div className="mh-success">
        <h3>No active interview session</h3>
        <p>There is no interview session running right now. When Make It Happen Philly starts your next session it will appear here automatically — keep using this same link.</p>
      </div>
      {data.nextUpcoming && <UpcomingPrep u={data.nextUpcoming} />}
      <RegBar />
    </div></div>
  );

  const cfg = data.config;
  const total = data.candidates.length;
  const recorded = data.candidates.filter((c) => c.decision);
  const done = recorded.length;
  const allRecorded = total > 0 && done === total;
  const pct = total ? Math.round((done / total) * 100) : 0;

  if (selected != null) {
    const idx = data.candidates.findIndex((c) => c.registrationId === selected);
    if (idx === -1) { setSelected(null); return null; }
    const c = data.candidates[idx];
    const cc = { ...c, token, submittedBy: supBy, onRefresh: load };
    return (
      <div className="mh"><div className="mh-wrap">
        {mode === "summary"
          ? <CompletedSummary c={cc} index={idx + 1} total={total} onBack={backToRoster} onCorrect={() => { setMode("decision"); setCorrecting(true); }} />
          : <CandidateDecision c={cc} config={cfg} index={idx + 1} total={total} correcting={correcting && !!c.decision} onBack={backToRoster} onSaved={onSaved} />}
      </div></div>
    );
  }

  const complete = async () => {
    try {
      const r = await completeHandoff(token, { submittedBy: supBy || undefined, regIds: recorded.map((c) => c.registrationId) });
      setReceipt(r.receipt);
    } catch { /* completion guardrail is enforced server-side; button only shows when eligible */ }
  };
  const submitMissing = async () => {
    if (!mName.trim()) return;
    await reportMissing(token, { name: mName, note: mNote, submittedBy: supBy || undefined });
    setMissing(false); setMName(""); setMNote("");
  };

  return (
    <div className="mh" data-testid="employer-handoff">
      <div className="mh-wrap">
        <header className="mh-head">
          <div className="mh-logo"><img src="/mihp-logo.png" alt="Make It Happen Philly" /></div>
          <div className="mh-agency">MAKE IT HAPPEN PHILLY</div>
          <h1 className="mh-headtitle">Interview Handoff</h1>
          <div className="mh-headsub">{data.employerName} · {data.interviewDateLabel}</div>
        </header>

        <div className="mh-progress">
          <span data-testid="progress-done"><strong>{done}</strong> of {total} completed</span>
          <span className="mh-progress-remain" data-testid="progress-remaining">{total - done} still to interview</span>
        </div>
        <div className="mh-track"><div className="mh-track-fill" style={{ width: `${pct}%` }} /></div>

        <RegBar />

        <div className="mh-field mh-interviewer">
          <label>Interviewer / supervisor</label>
          <select className="mh-select" value={supBy} onChange={(e) => setSupBy(e.target.value)} data-testid="handoff-supervisor">
            <option value="">Select interviewer (optional)…</option>
            {cfg.supervisors.map((s) => <option key={s.id} value={s.label}>{s.label}</option>)}
          </select>
        </div>

        <div className="mh-listhead"><h2>Candidates</h2><span>Tap a name to record</span></div>

        <div className="mh-list" data-testid="roster-list">
          {data.candidates.map((c, i) => {
            const dn = !!c.decision;
            return (
              <button key={c.registrationId} className={`mh-row${dn ? " done" : ""}`} onClick={() => openCandidate(c)} data-testid={`roster-row-${c.registrationId}`}>
                <span className="mh-ord">{i + 1}</span>
                <span className="mh-rowmain">
                  <span className="mh-name" data-testid={`roster-name-${c.registrationId}`}>{c.name}</span>
                  {dn && recordedBy(c.decision) && <span className="mh-rec" data-testid={`roster-recordedby-${c.registrationId}`}>Recorded by {c.decision.submittedBy}</span>}
                </span>
                <span className="mh-state">
                  {dn ? <Check size={17} className="mh-check" data-testid={`roster-check-${c.registrationId}`} />
                      : <ChevronRight size={17} className="mh-chev" data-testid={`roster-chevron-${c.registrationId}`} />}
                </span>
              </button>
            );
          })}
        </div>

        {allRecorded
          ? <button className="mh-complete-btn" onClick={complete} data-testid="complete-handoff-btn">Complete Interview Handoff</button>
          : <p className="mh-complete-note" data-testid="completion-note">The handoff can be completed once every candidate is recorded.</p>}

        <button className="mh-textlink" onClick={() => setMissing(true)} data-testid="report-missing-btn">Report someone not on the list</button>
        {data.nextUpcoming && <UpcomingPrep u={data.nextUpcoming} />}
      </div>

      {missing && (
        <div className="modal-scrim" onClick={() => setMissing(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>Report someone not on the list</h2><button className="x" onClick={() => setMissing(false)}><X size={20} /></button></div>
            <div className="modal-body">
              <p className="modal-intro">Only for someone who says they are with MIHP but is not on this roster. This creates an internal verification item — it does not register them.</p>
              <div className="field"><label>Name they gave</label><input value={mName} onChange={(e) => setMName(e.target.value)} data-testid="missing-name" /></div>
              <div className="field full" style={{ marginTop: 12 }}><label>Note (optional)</label><input value={mNote} onChange={(e) => setMNote(e.target.value)} data-testid="missing-note" /></div>
            </div>
            <div className="modal-foot">
              <button className="btn ghost" onClick={() => setMissing(false)}>Cancel</button>
              <button className="btn primary" onClick={submitMissing} data-testid="missing-submit">Send to MIHP</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UpcomingPrep({ u }) {
  return (
    <div className="upcoming-prep" data-testid="upcoming-prep">
      <div className="up-head">
        <span className="up-tag">Next Interview — registration in progress</span>
        <span className="up-title">{u.interviewDateLabel}</span>
        <span className="up-when">· {u.time}</span>
        <span className="up-count" data-testid="upcoming-count">{u.count} registered · Session {u.sessionCode}</span>
      </div>
      {u.candidates.length ? (
        <ul>
          {u.candidates.map((c) => (
            <li key={c.registrationId} data-testid={`upcoming-row-${c.registrationId}`}>
              <span className="up-name">{c.name}</span>
              {c.preferredShift ? <span className="up-shift">{c.preferredShift}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="up-empty">No one has registered for this interview yet.</div>
      )}
    </div>
  );
}
